import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { Button, ConfirmationModal, PasswordField, TextButton, Typography } from 'seagull';
import { routes } from '~/constants';
import { useEventReporter } from '~/event-reporter';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import XeroSignInButton from '~/pages/unAuthorizedPages/components/XeroSignInButton/XeroSignInButton';
import { useStore, useStoreActions } from '~/store/store-context';
import GoogleButton from '../GoogleButton/GoogleButton';
import IntuitButton from '../IntuitButton/IntuitButton';
import styles from './SecureLoginModal.module.scss';
const SECURE_LOGIN_SET_FLAG = 'secure-login-set-success';
export const SecureLoginModal = (param)=>{
    let { dismissModal, isDismissDisabled = false } = param;
    const { api, logBI } = useStoreActions();
    const { reportToHeap } = useEventReporter();
    const [step, setStep] = useState('selectIdentity');
    const form = useForm({
        mode: 'all',
        defaultValues: {
            password: '',
            ['confirm-password']: ''
        }
    });
    const { handleSubmit } = form;
    const { SET_IDENTITY_ONLY_TOKEN_FE } = useFeatureFlags();
    const setPassword = async (data)=>{
        logBI(18102);
        logBI(18902);
        reportToHeap('secure-login', 'click', 'client-click-set-login-password');
        const { password } = data;
        if (SET_IDENTITY_ONLY_TOKEN_FE) {
            await api.auth.setPassword(password);
            localStorage.setItem(SECURE_LOGIN_SET_FLAG, 'true');
        } else {
            await api.auth.editUserDetails({
                password
            });
        }
        dismissModal(true);
    };
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": "secure-login-modal",
        children: [
            step === 'selectIdentity' && /*#__PURE__*/ _jsx(SelectIdentityTypeStep, {
                dismissModal: dismissModal,
                onCreatePassword: ()=>{
                    logBI(18901);
                    setStep('setPassword');
                },
                isDismissDisabled: isDismissDisabled
            }),
            step === 'setPassword' && /*#__PURE__*/ _jsx(SetPasswordStep, {
                dismissModal: dismissModal,
                onClickBack: ()=>setStep('selectIdentity'),
                onContinue: handleSubmit(setPassword),
                form: form,
                isDismissDisabled: isDismissDisabled
            })
        ]
    });
};
const SelectIdentityTypeStep = (param)=>{
    let { onCreatePassword, dismissModal, isDismissDisabled } = param;
    const userInfo = useStore((state)=>state.userInfo);
    const history = useHistory();
    const { logBI } = useStoreActions();
    const ensureEmail = userInfo === null || userInfo === void 0 ? void 0 : userInfo.email();
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        title: "Secure login required",
        content: /*#__PURE__*/ _jsxs("div", {
            className: styles.container,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: styles.textContainer,
                    children: /*#__PURE__*/ _jsx(Typography, {
                        children: "Creating a Service Provider account requires a secure authentication method. Choose from the following options:"
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.formContainer,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: styles.formComponentsWrapper,
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                skin: "darkSecondary",
                                size: "medium",
                                fluid: true,
                                testId: "create-password-btn",
                                onClick: onCreatePassword,
                                children: "Create a password"
                            }),
                            /*#__PURE__*/ _jsx(GoogleButton, {
                                ensureEmail: ensureEmail,
                                onAuthSuccess: ()=>{
                                    logBI(18903);
                                    history.push(routes.home);
                                }
                            }),
                            /*#__PURE__*/ _jsx(XeroSignInButton, {
                                location: window.location,
                                mode: "signIn",
                                ensureEmail: ensureEmail
                            }),
                            /*#__PURE__*/ _jsx(IntuitButton, {
                                mode: "signIn",
                                ensureEmail: ensureEmail
                            })
                        ]
                    })
                })
            ]
        }),
        secondaryButtonLabel: !isDismissDisabled ? 'Cancel' : undefined,
        secondaryButtonTestId: "cancel-btn",
        onSecondaryButtonClick: ()=>{
            if (isDismissDisabled) return;
            dismissModal(false);
        },
        cancelButtonDisabled: isDismissDisabled,
        onCloseButtonClick: ()=>{
            if (isDismissDisabled) return;
            dismissModal(false);
        },
        testId: "select-identity-step"
    });
};
const SetPasswordStep = (param)=>{
    let { onClickBack, onContinue, form, dismissModal, isDismissDisabled } = param;
    const { control, getValues } = form;
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        title: "Set your password",
        content: /*#__PURE__*/ _jsxs("div", {
            className: styles.container,
            children: [
                /*#__PURE__*/ _jsx(TextButton, {
                    underline: "none",
                    testId: "back-btn",
                    onClick: onClickBack,
                    children: '< Back'
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.formContainer,
                    children: /*#__PURE__*/ _jsxs("form", {
                        className: styles.formComponentsWrapper,
                        children: [
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "password",
                                defaultValue: "",
                                rules: {
                                    required: true,
                                    minLength: {
                                        value: 10,
                                        message: 'Use 10 or more characters'
                                    },
                                    deps: 'confirm-password'
                                },
                                render: (param)=>{
                                    let { field, fieldState } = param;
                                    var _fieldState_error;
                                    return /*#__PURE__*/ _jsx(PasswordField, _object_spread_props(_object_spread({}, field), {
                                        error: !!fieldState.error,
                                        helperText: (_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message,
                                        testId: "password",
                                        label: "Password"
                                    }));
                                }
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "confirm-password",
                                defaultValue: "",
                                rules: {
                                    required: true,
                                    validate: (value)=>{
                                        const doPasswordsMatch = getValues('password') === value;
                                        return doPasswordsMatch || 'Passwords do not match';
                                    }
                                },
                                render: (param)=>{
                                    let { field, fieldState } = param;
                                    var _fieldState_error;
                                    return /*#__PURE__*/ _jsx(PasswordField, _object_spread_props(_object_spread({}, field), {
                                        error: !!fieldState.error,
                                        helperText: (_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message,
                                        testId: "confirm-password",
                                        label: "Re-enter password"
                                    }));
                                }
                            })
                        ]
                    })
                })
            ]
        }),
        primaryButtonLabel: "Continue",
        primaryButtonTestId: "continue-btn",
        onPrimaryButtonClick: onContinue,
        secondaryButtonLabel: !isDismissDisabled ? 'Cancel' : undefined,
        secondaryButtonTestId: "cancel-btn",
        onSecondaryButtonClick: ()=>{
            if (isDismissDisabled) return;
            dismissModal(false);
        },
        onCloseButtonClick: ()=>{
            if (isDismissDisabled) return;
            dismissModal(false);
        },
        cancelButtonDisabled: isDismissDisabled,
        testId: "set-password-step"
    });
};
